<template>
  <modal-wrapper ref="modalRef" @ok-button-click="handleModalOk">
    <div class="modal-content-container">
      <h4>{{ $t('filesOverview.newInspectionDueDate') }}</h4>
      <PVDatePicker class="date-picker" @value-change="handleDateUpdated" />
    </div>
  </modal-wrapper>
</template>

<script lang="ts" setup>
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import axios from 'axios';
  import endpointsList from '@/helpers/endpointsList';
  import { IChangeInspectionDueDatesRequest } from 'types';
  import { useModalWrapper } from '../ModalWrapper';
  import { DatePickerValueChangeEvent } from '@/interfaces';
  import { isSingleDateEvent } from '@/utils';

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();

  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  const props = defineProps<{
    fileIds: string[];
    projectId: string;
  }>();

  const emit = defineEmits<{
    (e: 'inspection-due-dates-updated'): void;
  }>();

  const date = ref<Date | undefined>(undefined);

  function handleDateUpdated(newDate: DatePickerValueChangeEvent) {
    if (!isSingleDateEvent(newDate)) return;
    date.value = newDate;
  }

  async function handleModalOk() {
    if (!date.value) return;

    const requestBody: IChangeInspectionDueDatesRequest = {
      fileIds: props.fileIds,
      projectId: props.projectId,
      newInspectionDueDate: date.value.toISOString(),
    };

    try {
      await axios.patch(
        endpointsList.documentsUploads.changeInspectionDueDates,
        requestBody,
      );
      emit('inspection-due-dates-updated');

      createSuccessToast(t('documentDetailView.inspectionDueDateChanged'));
    } catch (err) {
      createErrorToast(t('filesOverview.errors.inspectionDueDateChangeError'));
    }
  }
</script>

<style lang="scss" scoped>
  .modal-content-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    row-gap: 20px;

    .date-picker {
      min-width: 200px;
      height: 52px;
    }
  }
</style>
